import { defineComponent, ref, onMounted, computed } from "vue";
import TextEditor from "./component/text-editor/ckeditor.vue";

export default defineComponent({
  name: "c-textarea",
  components: {
    TextEditor,
  },

  props: {
    value: {
      type: [String, Number, Boolean],
      default: "",
    },
    label: { type: String, default: "" },
    type: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    error: { type: [Boolean, String], default: false },
    lowercase: { type: Boolean, default: false },
    uppercase: { type: Boolean, default: false },
    number: { type: Boolean, default: false },
    integer: { type: Boolean, default: false },
    percentage: { type: Boolean, default: false },
    decimal: { type: Boolean, default: false },
    format: { type: [String, RegExp], default: "" },
    length: { type: [String, Number], default: null },
    not: { type: Object, default: () => ({}) },
    readonly: { type: Boolean, default: false },
    string: { type: Boolean, default: false },
    latLong: { type: Boolean, default: false },
    version: { type: Boolean, default: false },
    minDate: { type: String, default: "" },
    schema: { type: Function, default: () => ({}) },
    refIds: { type: String, default: "" },
    required: { type: Boolean, default: false },
    rows: { type: Number, default: 5 },
    textEditor: { type: Boolean, default: false },
    editorOption: [],
  },

  emits: ["update:value", "enter"],

  setup(props, { emit }) {
    const showPopup = ref(false);
    const autocompleteValue = "off";
    const isRequired = ref(false);
    const updatedLabel = ref("");
    const isError = computed(() => {
      return props.error;
    });

    const listRequired = () => {
      if (props.label && props.label.includes("*")) {
        updatedLabel.value = props.label.replace(/\*/g, "");
        isRequired.value = true;
      } else {
        isRequired.value = false;
        updatedLabel.value = props.label;
      }
    };

    onMounted(() => {
      listRequired();
    });

    const onEmits = (e: InputEvent) => {
      const value = (e.target as HTMLInputElement).value;
      emit("update:value", value);
    };

    const onKeypress = (evt: KeyboardEvent) => {
      if (props.lowercase) {
        emit(
          "update:value",
          (evt.target as HTMLInputElement).value.toLowerCase()
        );
      }

      if (props.uppercase) {
        emit(
          "update:value",
          (evt.target as HTMLInputElement).value.toUpperCase()
        );
      }

      if (props.format) {
        let isValid = true;
        if (typeof props.format === "string") {
          const dynamicRegex = new RegExp(props.format, "i");
          const inputValue =
            (evt.target as HTMLInputElement).value +
            String.fromCharCode(evt.keyCode);
          isValid = dynamicRegex.test(inputValue);
        } else if (props.format instanceof RegExp) {
          isValid = props.format.test((evt.target as HTMLInputElement).value);
        }
        if (!isValid) {
          evt.preventDefault();
        }
      }

      if (props.length) {
        if (
          Number((evt.target as HTMLInputElement).value.length) ===
          Number(props.length)
        ) {
          evt.preventDefault();
        }
      }

      if (props.type == "letter") {
        if (!/[a-z0-9]/.test(evt.key)) {
          evt.preventDefault();
        }
      }

      if ((evt.target as HTMLInputElement).value.startsWith(" ")) {
        evt.preventDefault();
      }

      if (props.string) {
        if (!/^[a-zA-Z\s]*$/.test(evt.key)) {
          evt.preventDefault();
        }
      }

      if (props.latLong) {
        if (
          (isNaN(parseFloat(evt.key)) && evt.key !== ".") || // Prevent non-digits and multiple dots
          (evt.key === "." &&
            (evt.target as HTMLInputElement).value.includes(".")) // Prevent duplicate dots
        ) {
          evt.preventDefault();
        }
      }

      if (props.version) {
        if (isNaN(parseFloat(evt.key)) && evt.key !== ".") {
          evt.preventDefault();
        }
      }

      if (props.type == "number" || props.number) {
        const spaceValue = (evt.target as HTMLInputElement).value.replace(
          /\s/g,
          ""
        );
        emit("update:value", spaceValue);

        if (
          !/^\d+(\.\d*)?$/.test(spaceValue) ||
          isNaN(parseFloat(spaceValue))
        ) {
          emit("update:value", null);
        }

        if ((evt.target as HTMLInputElement).value && props.integer) {
          if ((evt.target as HTMLInputElement).value.startsWith("0")) {
            emit("update:value", null);
            evt.preventDefault();
          }

          if (integerRead((evt.target as HTMLInputElement).value)) {
            if (evt.key === ".") {
              evt.preventDefault();
            }
          } else {
            evt.preventDefault();
          }
        }

        if (checkDots(evt)) {
          evt.preventDefault();
        }

        if (
          Number((evt.target as HTMLInputElement).value.length) ===
            (props.length || 20) &&
          !props.percentage
        ) {
          evt.preventDefault();
        }

        if (
          (props.percentage &&
            Number((evt.target as HTMLInputElement).value) >= 100) ||
          (pasteDecimal() && !props.decimal)
        ) {
          if (!pasteDecimal()) {
            emit("update:value", 0);
          }

          evt.preventDefault();
        }

        if (!props.decimal && pasteDecimal() && !props.percentage) {
          evt.preventDefault();
        }

        if (evt) {
          const charCode = evt.which ? evt.which : evt.keyCode;
          if (
            charCode > 31 &&
            (charCode < 48 || charCode > 57) &&
            charCode !== 46
          ) {
            evt.preventDefault();
          }
        } else {
          // Handle the case when evt is undefined
          return;
        }
      }

      props.schema();
    };

    const validateType = (type: string) => {
      if (type === "letter") {
        return "text";
      }
      return type;
    };

    const onEnter = () => {
      emit("enter");
    };

    const integerRead = (str: string) => {
      const integerPattern = /^-?\d+$/;
      return integerPattern.test(str);
    };

    const copyToClipboard = () => {
      const textArea = document.createElement("textarea");
      textArea.value = String(props.value); // Convert props.value to string explicitly
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      showPopup.value = true;

      setTimeout(() => {
        showPopup.value = false;
      }, 1500);
    };

    const pasteDecimal = () => {
      const numStr = String(props.value);
      const decimalIndex = numStr.indexOf(".");

      if (decimalIndex === -1 || decimalIndex === numStr.length - 1) {
        return false;
      }

      const decimalPart = numStr.substring(decimalIndex + 1);

      if (decimalPart.length >= 2) {
        return true;
      } else {
        return false;
      }
    };

    const checkDots = (evt: KeyboardEvent) => {
      const dotCount =
        ((evt.target as HTMLInputElement).value &&
          (evt.target as HTMLInputElement).value.match(/\./g)) ||
        [];

      return (dotCount && evt.key === "." && true) || false;
    };

    const isFloat = (value: string) => {
      const floatValue = parseFloat(value);

      if (!isNaN(floatValue) && typeof floatValue === "number") {
        return floatValue % 1 !== 0;
      }

      return false;
    };

    const handleKeyDown = (evt: KeyboardEvent) => {
      if (props.type == "number" || props.number) {
        if (evt.ctrlKey || evt.metaKey) {
          evt.target?.addEventListener("paste", (pasteEvent) => {
            // Type assertion to inform TypeScript that pasteEvent is a ClipboardEvent
            const clipboardData = (pasteEvent as ClipboardEvent).clipboardData;

            if (clipboardData) {
              const pastedText = clipboardData.getData("text");

              // Remove spaces from the pasted text
              const textWithoutSpaces = pastedText.replace(/\s/g, "");

              if (
                !/^[\d.]+$/.test(textWithoutSpaces) ||
                isNaN(Number(textWithoutSpaces))
              ) {
                emit("update:value", 0);
              }
            }
          });
        }
      }
    };

    const updateEditor = (content: string) => {
      emit("update:value", content);

      props.schema();
    };
    return {
      isRequired,
      updatedLabel,
      showPopup,
      autocompleteValue,
      isError,
      onEmits,
      onKeypress,
      validateType,
      onEnter,
      integerRead,
      copyToClipboard,
      pasteDecimal,
      checkDots,
      isFloat,
      handleKeyDown,
      updateEditor,
    };
  },
});
